import PropTypes from 'prop-types';
import { withErrorBoundary } from 'react-error-boundary';
import { useState, useEffect } from 'react';

import LdsErrorFallback from '../LdsErrorFallback/index.jsx';
import YoutubeDefault from './YoutubeDefault.jsx';
import YoutubeInterstitial from './YoutubeInterstitial.jsx';
import KalturaPlayer from './Kaltura.jsx';
import { useId } from 'react';
function LdsVideoPlayer({
  player,
  videoId,
  targetId,
  interstitial,
  chromeless,
  partnerId,
  uiConfId,
  title = 'Youtube Video Player',
  errorMessage = 'Could not load video player',
  interstitialText,
  buttonText,
  height = '100%',
  width = '100%',
  autoplay = false,
  youtubeObjectReady,
  seekToPositionKalturaReady,
  ...rest
}) {
  // id will be used to generate unique CSS for each video player instance
  const id = useId(); // TODO: This is a react 18 feature and needs to be removed for older version support
  const [generatedCss, setGeneratedCss] = useState('');

  function renderPlayer() {
    switch (player) {
      case 'youtube':
        if (interstitial) {
          return (
            <YoutubeInterstitial
              autoplay={autoplay}
              videoId={videoId}
              interstitialText={interstitialText}
              buttonText={buttonText}
              title={title}
              youtubeObjectReady={youtubeObjectReady}
              {...rest}
            />
          );
        }
        return (
          <YoutubeDefault
            autoplay={autoplay}
            videoId={videoId}
            title={title}
            youtubeObjectReady={youtubeObjectReady}
            {...rest}
          />
        );
      case 'kaltura':
        return (
          <KalturaPlayer
            key={`${videoId}_${partnerId}_${targetId}_${chromeless}_${uiConfId}`}
            videoId={videoId}
            partnerId={partnerId}
            targetId={targetId}
            chromeless={chromeless}
            uiConfId={uiConfId}
            autoplay={autoplay}
            seekToPositionKalturaReady={seekToPositionKalturaReady}
            {...rest}
          />
        );
      default:
        throw new Error(errorMessage);
    }
  }

  // CLS Optimization: generate approximate container query heights for video
  // player based on this tool: https://googlechromelabs.github.io/layout-shift-terminator/
  function generateCSS(id) {
    const aspectRatio = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--lds-video-aspect-ratio') || '16/9';
    const [width, height] = aspectRatio.split('/');
    const ratio = height / width;

    const breakpoints = [
      1536, 1366, 1200, 1100, 1000, 900, 800, 768, 700, 600, 500, 414, 375, 360,
    ];
    let css = `
      @supports (contain: inline-size) {
        #layout-shift-termination-${id} { container-type: inline-size; }
    `;

    breakpoints.forEach((bp) => {
      const minHeight = bp * ratio;
      css += `
        @container (max-width: ${bp}px) {
          #layout-shift-termination-${id} > .layout-shift-terminated { min-height:${minHeight}px; }
        }
      `;
    });

    css += '}';
    return css;
  }

  useEffect(() => {
    const css = generateCSS(id);
    setGeneratedCss(css);
  }, [id]);

  return (
    <div id={`layout-shift-termination-${id}`} style={{ height, width }}>
      <style dangerouslySetInnerHTML={{ __html: generatedCss }} />
      <div className="layout-shift-terminated"> {renderPlayer()}</div>
    </div>
  );
}

LdsVideoPlayer.propTypes = {
  videoId: PropTypes.string.isRequired,
  player: PropTypes.oneOf(['youtube', 'kaltura']).isRequired,
  height: PropTypes.string,
  width: PropTypes.string,
  targetId: PropTypes.string,
  interstitial: PropTypes.bool,
  chromeless: PropTypes.bool,
  errorMessage: PropTypes.string,
  partnerId: (props, propName, componentName) => {
    if (props.player === 'kaltura' && !props.partnerId) {
      return new Error(
        `Invalid prop ${propName} supplied to ${componentName}. When player is 'kaltura', ${propName} is required.`
      );
    }
    return null;
  },
  uiConfId: (props, propName, componentName) => {
    if (props.player === 'kaltura' && !props.uiConfId) {
      return new Error(
        `Invalid prop ${propName} supplied to ${componentName}. When player is 'kaltura', ${propName} is required.`
      );
    }
    return null;
  },
  interstitialText: PropTypes.string,
  buttonText: PropTypes.string,
  duration: PropTypes.string,
  startTime: PropTypes.string,
  chapterTitle: PropTypes.string,
  thumbnail: PropTypes.bool,
};

// This higher order error boundary component will catch errors thrown by
// renderPlayer() and any of the components it renders.
export default withErrorBoundary(LdsVideoPlayer, {
  fallbackRender: (props) => (
    <LdsErrorFallback
      {...props}
      resetButton={true}
      errorMessagePrefix="Encountered an error:"
    />
  ),
});
